import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3, FileText, Calendar, DollarSign, Users, Vote,
  MessageSquare, Settings, TrendingUp, AlertTriangle,
  Download, User, X,
  ClipboardCheck
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { Card, SectionHeader, EmptyState } from '../ui';
import { hoaProfile } from '../../lib/hoaProfile';
import { motion, AnimatePresence } from 'framer-motion';
import MemberLibrary from '../member/MemberLibrary';
import { activityService, ACTION_TYPES } from '../../services/activityService';
import MessageCenter from '../admin/MessageCenter';
import EventManagement from '../admin/EventManagement';
import { announcementService } from '../../services/announcementService';
import { supabase } from '../../lib/supabase';
import { messagingService } from '../../services/messagingService';
import MembersPage from '../admin/MembersPage';
import InsightDigest from '../admin/InsightDigest';
import ACCReview from '../common/ACCReview';
import { canSeeACCReview } from '../../lib/accVisibility';

const BoardPortal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const { tabVisibility, user, impersonating } = useAuth();
  const { hoa } = useHOA();
  const [notifications, setNotifications] = useState({});
  const notificationFetchInFlightRef = React.useRef(false);

  React.useEffect(() => {
    if (!user || !hoa?.id) return;
    const fetchNotifications = async () => {
      if (impersonating || notificationFetchInFlightRef.current) return;
      notificationFetchInFlightRef.current = true;
      try {
        const count = await messagingService.getUnreadThreadCount(user, user.portalRole || 'board', hoa.id);
        setNotifications({ messages: count });
      } catch (error) {
        console.warn('Unread message refresh failed:', error?.message || error);
      } finally {
        notificationFetchInFlightRef.current = false;
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);

    const handleRead = () => fetchNotifications();
    window.addEventListener('messages_read', handleRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('messages_read', handleRead);
    };
  }, [user, impersonating, hoa?.id]);

  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'insight-digest', label: 'Insight Digest', icon: TrendingUp },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'meetings', label: 'Meetings', icon: Calendar },
    { id: 'financials', label: 'Financials', icon: DollarSign },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'voting', label: 'Voting', icon: Vote },
    ...(canSeeACCReview(user) ? [{ id: 'accReview', label: 'ACC Review', icon: ClipboardCheck }] : []),
    { id: 'messages', label: 'Inbox', icon: MessageSquare },
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const tabs = allTabs.filter(tab => {
    const vis = tabVisibility?.board;
    return !vis || vis[tab.id] === undefined || vis[tab.id];
  });

  const [memberCount, setMemberCount] = useState('—');
  const [nextMeeting, setNextMeeting] = useState('Not scheduled');

  React.useEffect(() => {
    if (!hoa?.id) return;
    const loadDashboardStats = async () => {
      try {
        const { count } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('hoa_id', hoa.id)
          .eq('is_active', true);
        if (count !== null) setMemberCount(count);

        const { data: meetings } = await supabase
          .from('board_meetings')
          .select('scheduled_at')
          .eq('hoa_id', hoa.id)
          .eq('status', 'scheduled')
          .gt('scheduled_at', new Date().toISOString())
          .order('scheduled_at', { ascending: true })
          .limit(1);

        if (meetings && meetings.length > 0) {
          const date = new Date(meetings[0].scheduled_at);
          const formatted = date.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric',
            timeZone: 'America/Denver'
          });
          const time = date.toLocaleTimeString('en-US', {
            hour: 'numeric', minute: '2-digit',
            timeZone: 'America/Denver'
          });
          setNextMeeting(`${formatted} @ ${time}`);
        }
      } catch (err) {
        console.error('Dashboard stats error:', err);
      }
    };
    loadDashboardStats();
  }, [hoa?.id]);

  const content = {
    dashboard: <BoardDashboard memberCount={memberCount} nextMeeting={nextMeeting} onNavigateToTab={(t) => setSearchParams({ tab: t })} currentUser={user} />,
    documents: <MemberLibrary onOpenAISearch={() => {}} />,
    events: <CommunityEvents />,
    meetings: <MeetingManagement nextMeeting={nextMeeting} profile={hoaProfile(hoa)} />,
    financials: <FinancialOverview memberCount={memberCount} profile={hoaProfile(hoa)} />,
    members: <MembersPage />,
    voting: <VotingCenter profile={hoaProfile(hoa)} />,
    accReview: <ACCReview />,
    messages: <MessageCenter />,
    'insight-digest': <InsightDigest />,
    profile: <BoardProfile />,
    settings: <BoardSettings />,
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  return (
    <PortalLayout title="Board Portal" tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} userRole="board" notifications={notifications}>
      {content[activeTab]}
    </PortalLayout>
  );
};




/* ── BOARD DASHBOARD ────────────────────────────────────── */
const BoardDashboard = ({ memberCount, nextMeeting, onNavigateToTab, currentUser }) => {
  const { user } = useAuth();
  const { hoa } = useHOA();
  const firstName = user?.name?.split(' ')[0] || 'Board Member';

  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', body: '', tag: 'General' });
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});

  const fetchA = React.useCallback(async () => {
    if (!hoa?.id) return;
    const data = await announcementService.getAllAnnouncements(hoa.id);
    setAnnouncements(data);
  }, [hoa?.id]);

  React.useEffect(() => {
    fetchA();
  }, [fetchA]);

  const handlePostAnnouncement = async (e) => {
    e.preventDefault();
    if (!newAnnouncement.title.trim() || !newAnnouncement.body.trim()) return;
    await announcementService.createAnnouncement(user, newAnnouncement, hoa.id);
    setNewAnnouncement({ title: '', body: '', tag: 'General' });
    fetchA();
  };

  const handleComment = async (id) => {
    if (!commentText[id]?.trim()) return;
    await announcementService.addComment(id, user, commentText[id]);
    setCommentText({ ...commentText, [id]: '' });
    fetchA();
  };

  const toggleComments = (id) => {
    setShowComments(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Open items the board is tracking: hoas.settings.priority_items (hoaProfile).
  const urgentItems = hoaProfile(hoa).priorityItems.map((item, i) => ({ id: i + 1, ...item }));

  const severityStyles = {
    high:   'bg-red-50 border-l-4 border-l-red-400 text-red-900',
    medium: 'bg-amber-50 border-l-4 border-l-amber-400 text-amber-900',
  };

  const severityDot = {
    high:   'bg-red-400',
    medium: 'bg-amber-400',
  };

  // Format nextMeeting string for stat display
  const meetingDisplay = (() => {
    if (!nextMeeting || nextMeeting === 'Not scheduled') return 'TBD';
    try {
      const parts = nextMeeting.split(' @ ');
      return parts[0] || nextMeeting;
    } catch (e) {
      return nextMeeting;
    }
  })();

  return (
    <div className="space-y-8">

      {/* ── HERO ── */}
      <section className="relative h-auto min-h-[280px] py-10 md:py-0 md:h-[280px] rounded-3xl overflow-hidden warm-tactile-shadow">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/hero-bg.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-6 md:px-14 max-w-2xl">
          <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-3">
            {hoaProfile(hoa).shortName} · Board Operations
          </p>
          <h2 className="text-4xl font-extrabold text-white tracking-tight mb-3">
            Welcome, {firstName}
          </h2>
          <p className="text-white/75 text-sm font-medium mb-8 leading-relaxed">
            {urgentItems.filter(i => i.severity === 'high').length} items require your immediate attention.
          </p>
          <div className="flex flex-wrap gap-3 md:gap-4">
            <button
              onClick={() => onNavigateToTab('meetings')}
              className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all shadow-lg"
            >
              Board Meetings
            </button>
            <button
              onClick={() => onNavigateToTab('financials')}
              className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-7 py-3 rounded-full font-bold text-sm hover:bg-white/20 transition-all"
            >
              Financials
            </button>
          </div>
        </div>
      </section>

      {/* ── STAT ROW ── */}
      <section className="bg-section-bg p-6 rounded-[2.5rem]">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">

          <div className="min-h-[48px] py-4 pr-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">group</span>
            </div>
            <p className="text-3xl font-extrabold text-primary-dark leading-none">{memberCount || '—'}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Active Members</p>
          </div>

          <div className="min-h-[48px] py-4 px-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">savings</span>
            </div>
            <p className="text-3xl font-extrabold text-secondary leading-none">{hoaProfile(hoa).reserveShortLabel || '—'}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Reserve Fund</p>
          </div>

          <div className="min-h-[48px] py-4 px-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-red-400">warning</span>
            </div>
            <p className="text-3xl font-extrabold text-red-500 leading-none">{urgentItems.length}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Pending Actions</p>
          </div>

          <div className="min-h-[48px] py-4 pl-6 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">calendar_today</span>
            </div>
            <p className="text-3xl font-extrabold text-primary-dark leading-none">{meetingDisplay}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Next Meeting</p>
          </div>

        </div>
      </section>

      {/* ── MAIN GRID ── */}
      <section className="bg-section-bg p-6 rounded-[2.5rem] space-y-6">

        {/* Row 1 — Priority Items + Quick Nav */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Priority Action Items — spans 2 cols */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-8 warm-tactile-shadow border border-red-100/50">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-red-400">priority_high</span>
              </div>
              <div>
                <h5 className="text-xl font-bold text-primary-dark">Priority Action Items</h5>
                <p className="text-xs font-medium text-slate-400">Requires board attention</p>
              </div>
              <span className="ml-auto bg-red-50 text-red-600 text-xs font-bold px-3 py-1 rounded-full border border-red-100">
                {urgentItems.length} open
              </span>
            </div>
            <div className="space-y-3">
              {urgentItems.map(item => (
                <div key={item.id} className={`p-4 rounded-xl ${severityStyles[item.severity]} flex items-start gap-3`}>
                  <div className={`w-2 h-2 rounded-full ${severityDot[item.severity]} mt-1.5 flex-shrink-0`} />
                  <div>
                    <p className="text-sm font-bold">{item.title}</p>
                    <p className="text-xs font-medium opacity-75 mt-0.5 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Board Navigation */}
          <div className="bg-[#DFE7D8] rounded-2xl p-8 warm-tactile-shadow border border-emerald-300/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center border border-white shadow-sm">
                <span className="material-symbols-outlined text-2xl text-emerald-900">dashboard</span>
              </div>
              <h5 className="text-xl font-bold text-emerald-950">Board Tools</h5>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Documents', icon: 'description', tab: 'documents' },
                { label: 'Financials', icon: 'trending_up', tab: 'financials' },
                { label: 'Members', icon: 'group', tab: 'members' },
                { label: 'Meetings', icon: 'calendar_today', tab: 'meetings' },
                { label: 'Messages', icon: 'chat_bubble', tab: 'messages' },
              ].map(item => (
                <button
                  key={item.tab}
                  onClick={() => onNavigateToTab(item.tab)}
                  className="w-full flex items-center justify-between p-3.5 bg-white/70 hover:bg-white rounded-xl border border-white transition-all group/btn"
                >
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-xl text-emerald-800">{item.icon}</span>
                    <span className="text-sm font-bold text-emerald-950">{item.label}</span>
                  </div>
                  <span className="material-symbols-outlined text-sm text-emerald-600 transition-transform group-hover/btn:translate-x-1">chevron_right</span>
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Row 2 — Post Announcement */}
        <div className="bg-white rounded-2xl p-8 warm-tactile-shadow border border-slate-200/50">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl text-secondary">campaign</span>
            </div>
            <div>
              <h5 className="text-xl font-bold text-primary-dark">Post Announcement</h5>
              <p className="text-xs font-medium text-slate-400">Broadcast to all members</p>
            </div>
          </div>
          <form onSubmit={handlePostAnnouncement} className="space-y-4">
            <input
              type="text"
              placeholder="Announcement title..."
              value={newAnnouncement.title}
              onChange={e => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-main-bg text-sm font-medium text-primary-dark placeholder-slate-400 focus:outline-none focus:border-secondary transition-colors"
            />
            <textarea
              placeholder="Write your message to the community..."
              value={newAnnouncement.body}
              onChange={e => setNewAnnouncement({ ...newAnnouncement, body: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-main-bg text-sm font-medium text-primary-dark placeholder-slate-400 focus:outline-none focus:border-secondary transition-colors resize-none"
            />
            <div className="flex items-center justify-between">
              <select
                value={newAnnouncement.tag}
                onChange={e => setNewAnnouncement({ ...newAnnouncement, tag: e.target.value })}
                className="px-4 py-2.5 rounded-full border border-slate-200 bg-white text-xs font-bold text-slate-600 focus:outline-none focus:border-secondary"
              >
                <option>General</option>
                <option>Meeting</option>
                <option>Maintenance</option>
                <option>Infrastructure</option>
              </select>
              <button
                type="submit"
                disabled={!newAnnouncement.title || !newAnnouncement.body}
                className="bg-secondary text-white px-7 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
              >
                Post to Community
              </button>
            </div>
          </form>

          {/* Recent Announcements */}
          {announcements.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Recent Posts</p>
              {announcements.slice(0, 3).map(a => (
                <div key={a.id} className="p-4 rounded-xl bg-main-bg border border-slate-200/60">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-bold text-primary-dark">{a.title}</p>
                      <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{a.body}</p>
                    </div>
                    <button
                      onClick={() => toggleComments(a.id)}
                      className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex-shrink-0 hover:text-secondary transition-colors"
                    >
                      {showComments[a.id] ? 'Hide' : 'Comments'}
                    </button>
                  </div>
                  {showComments[a.id] && (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex gap-2">
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={commentText[a.id] || ''}
                        onChange={e => setCommentText({ ...commentText, [a.id]: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && handleComment(a.id)}
                        className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs font-medium focus:outline-none focus:border-secondary"
                      />
                      <button
                        onClick={() => handleComment(a.id)}
                        disabled={!commentText[a.id]?.trim()}
                        className="px-4 py-2 bg-secondary text-white rounded-full text-xs font-bold hover:brightness-110 transition-all disabled:opacity-50"
                      >
                        Post
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ACC Applications tile — board/acc members only */}
        {currentUser?.is_acc_member && (
          <div className="bg-[#1D2B20] rounded-2xl p-8 text-white warm-tactile-shadow relative overflow-hidden group">
            <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors duration-500" />
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center border border-white/10">
                  <span className="material-symbols-outlined text-2xl text-secondary">home_work</span>
                </div>
                <div>
                  <h5 className="text-lg font-bold">ACC Applications</h5>
                  <p className="text-white/60 text-xs font-medium">Architectural review queue</p>
                </div>
              </div>
              <button
                onClick={() => onNavigateToTab('documents')}
                className="flex items-center gap-2 bg-secondary text-white px-6 py-2.5 rounded-full font-bold text-sm hover:brightness-110 transition-all"
              >
                Review
                <span className="material-symbols-outlined text-sm">arrow_forward</span>
              </button>
            </div>
          </div>
        )}

      </section>
    </div>
  );
};

/* ── MEETINGS ─────────────────────────────────────────── */
// Association-specific agenda and location come from hoas.settings (hoaProfile);
// an association without them gets the standard items.
const DEFAULT_AGENDA = ['Call to Order', 'Approval of Previous Minutes', 'Financial Report', 'Old Business', 'New Business', 'Adjournment'];

const MeetingManagement = ({ nextMeeting = 'TBD @ TBD', profile }) => {
  const meetingDate = nextMeeting.includes('@') ? nextMeeting.split(' @ ')[0] : 'TBD';
  const meetingTime = nextMeeting.includes('@') ? nextMeeting.split(' @ ')[1] : 'TBD';
  const agenda = (profile?.meetingAgenda || DEFAULT_AGENDA).map((item, i) => ({ num: i + 1, item }));

  return (
    <div className="space-y-5">
      <Card>
        <SectionHeader icon={Calendar} title="Next Meeting: Annual HOA Meeting" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
          {[
            { label: 'Date', value: meetingDate },
            { label: 'Time', value: meetingTime },
            { label: 'Location', value: profile?.meetingLocation || 'TBD' },
          ].map((f, i) => (
            <div key={i} className="p-3 bg-white rounded-xl border border-slate-200 text-center">
              <div className="text-slate-400 text-xs mb-1">{f.label}</div>
              <div className="text-slate-900 text-sm font-semibold">{f.value}</div>
            </div>
          ))}
        </div>

        <h4 className="text-slate-900 font-semibold text-sm mb-3">Draft Agenda</h4>
        <div className="space-y-1.5">
          {agenda.map((a) => (
            <div key={a.num} className="flex items-center gap-3 p-2.5 bg-white rounded-lg border border-slate-200 text-sm">
              <span className="text-slate-400 font-mono text-xs w-5 text-right">{a.num}.</span>
              <span className="text-slate-800">{a.item}</span>
            </div>
          ))}
        </div>
        <button className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-secondary hover:brightness-110 text-white text-sm font-semibold rounded-xl transition-colors">
          <Download className="w-4 h-4" />
          Export Agenda
        </button>
      </Card>
    </div>
  );
};

/* ── FINANCIALS ───────────────────────────────────────── */
// Figures come from the association's own hoas.settings (hoaProfile); until an
// association has them, the tab explains where they will come from.
const FinancialOverview = ({ memberCount, profile }) => {
  const reserves = profile?.reserves;
  const cdLadder = reserves?.cd_ladder || [];
  const summaryCards = [
    reserves && { label: 'Total Reserves', value: reserves.total_label, sub: reserves.sub_label, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    profile?.duesRevenue && { label: 'Annual Dues Revenue', value: profile.duesRevenue.value_label, sub: `~${memberCount} units × ${profile.duesRevenue.unit_dues_label}`, color: 'text-secondary', bg: 'bg-secondary/10', border: 'border-secondary/30' },
    profile?.featuredProject && { label: profile.featuredProject.label, value: profile.featuredProject.value_label, sub: profile.featuredProject.sub_label, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  ].filter(Boolean);

  if (summaryCards.length === 0 && cdLadder.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="account_balance"
          title="No financial overview yet"
          body="Reserve, dues and project figures appear here once your association's financial details are set up. Upload financial reports to the Library in the meantime."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((s, i) => (
          <div key={i} className={`${s.bg} border ${s.border} rounded-xl p-5`}>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-slate-800 font-semibold text-sm mt-1">{s.label}</div>
            <div className="text-slate-500 text-xs">{s.sub}</div>
          </div>
        ))}
      </div>

      {cdLadder.length > 0 && (
      <Card>
        <SectionHeader icon={DollarSign} title="CD Ladder — Reserve Investments" color="text-emerald-700" accent="bg-emerald-50" />
        {reserves?.note && <div className="text-xs text-slate-500 mb-3">{reserves.note}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {['Institution', 'Term', 'Amount', 'Maturity', 'Rate'].map(h => (
                  <th key={h} className="text-left pb-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {cdLadder.map((cd, i) => (
                <tr key={i} className="hover:bg-white transition-colors">
                  <td className="py-2.5 pr-4 text-slate-900 font-medium">{cd.institution}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{cd.term}</td>
                  <td className="py-2.5 pr-4 text-slate-900 font-semibold">{cd.amount}</td>
                  <td className="py-2.5 pr-4 text-slate-600">{cd.maturity}</td>
                  <td className="py-2.5 text-emerald-700 font-semibold">{cd.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
};

/* ── VOTING ───────────────────────────────────────────── */
const VotingCenter = ({ profile }) => {
  const [vote, setVote] = useState(null);

  return (
    <Card>
      <SectionHeader icon={Vote} title="Board Voting" />
      {profile?.openMotion ? (
      <>
      <div className="p-5 bg-white rounded-xl border border-slate-200 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 text-xs rounded-full font-medium">Open Motion</span>
        </div>
        <h4 className="text-slate-900 font-semibold text-base mb-1.5">{profile.openMotion.title}</h4>
        <p className="text-slate-600 text-sm mb-1">{profile.openMotion.body}</p>
        <p className="text-slate-400 text-xs mb-4">Moved by: Pending · Second: Pending · Requires: Majority of quorum</p>

        <div className="flex gap-2">
          {['Approve', 'Reject', 'Table'].map(v => (
            <button
              key={v}
              onClick={() => setVote(v)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${vote === v
                ? v === 'Approve' ? 'bg-emerald-600 text-white border-emerald-600' : v === 'Reject' ? 'bg-red-600 text-white border-red-600' : 'bg-slate-600 text-white border-slate-600'
                : 'bg-white text-slate-700 border-slate-200 hover:border-forest-300'
                }`}
            >
              {v}
            </button>
          ))}
        </div>
        {vote && (
          <div className="mt-3 text-sm text-slate-600">
            Your vote: <strong className="text-slate-900">{vote}</strong> — awaiting other board members.
          </div>
        )}
      </div>
      </>
      ) : (
        <EmptyState icon="how_to_vote" title="No open motions" body="Motions the board opens for a vote will appear here." />
      )}
    </Card>
  );
};

/* CommunicationCenter removed and replaced by shared MessageCenter */

/* ── COMMUNITY EVENTS ─────────────────────────────────── */
const CommunityEvents = () => <EventManagement />;

const BoardProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showApprovalMock, setShowApprovalMock] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    title: user?.title || '',
    company: user?.company || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || ''
  });

  const handleSave = () => {
    setIsEditing(false);
    setShowApprovalMock(true);
    setTimeout(() => setShowApprovalMock(false), 4000);

    activityService.log(ACTION_TYPES.PROFILE_UPDATED, user, {
      details: 'Board portal profile update request'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SectionHeader icon={User} title="My Profile" color="text-secondary" accent="bg-secondary/10" />
        {!isEditing && (
          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-forest-300 text-slate-700 rounded-xl transition-all">
            <User className="w-4 h-4" /> Edit Profile
          </button>
        )}
      </div>

      <AnimatePresence>
        {showApprovalMock && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-sm">Profile Update Received</h4>
              <p className="text-xs mt-1">Your profile changes have been submitted to the HOA Board for approval. They will be reflected here once approved.</p>
            </div>
            <button onClick={() => setShowApprovalMock(false)} className="ml-auto text-amber-500 hover:text-amber-700"><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <Card>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Name</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 shadow-sm focus:border-secondary focus:ring-1 focus:ring-secondary rounded-lg text-base md:text-sm outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Title</label>
                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 shadow-sm focus:border-secondary focus:ring-1 focus:ring-secondary rounded-lg text-base md:text-sm outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Address</label>
                <input type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 shadow-sm focus:border-secondary focus:ring-1 focus:ring-secondary rounded-lg text-base md:text-sm outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Email</label>
                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 shadow-sm focus:border-secondary focus:ring-1 focus:ring-secondary rounded-lg text-base md:text-sm outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Phone</label>
                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 bg-white border border-slate-200 shadow-sm focus:border-secondary focus:ring-1 focus:ring-secondary rounded-lg text-base md:text-sm outline-none" />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4 border-t border-slate-100">
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors">Cancel</button>
              <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-secondary hover:brightness-110 text-white rounded-lg text-sm font-medium transition-colors">Save Request</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-w-lg">
            {[
              { label: 'Name', value: user?.name || '—' },
              { label: 'Email', value: user?.email || '—' },
              { label: 'Address', value: user?.address || '—' },
              { label: 'Phone', value: user?.phone || '—' },
            ].map((f, i) => (
              <div key={i} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
                <span className="text-slate-500 text-sm">{f.label}</span>
                <span className="text-slate-900 text-sm font-medium">{f.value}</span>
              </div>
            ))}
            <div className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200">
              <span className="text-slate-500 text-sm">Role</span>
              <span className="px-2.5 py-0.5 text-xs rounded-full border font-medium bg-secondary/10 text-forest-800 border-secondary/30">
                {user?.role === 'management' ? 'Management' : 'Board Member'}
              </span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const BoardSettings = () => (
  <Card>
    <SectionHeader icon={Settings} title="Board Settings" />
    <p className="text-slate-500 text-sm">Board-level configuration options — in development.</p>
  </Card>
);

export default BoardPortal;
